import { useCallback, useEffect, useState } from 'react';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import {
  EVENT_INDEX_PATH,
  diffEventIndex,
  loadCurrentEventPairs,
  loadPublishedEventIndex,
  publishEventIndex,
} from '../lib/eventIndexStore';

/**
 * Global event index (boat-first IA E1-T6, ra-8en2.1.6).
 *
 * The index is normally refreshed automatically whenever a race schedule is
 * published. This page exists for the two cases automation cannot cover: the
 * FIRST build (nothing has ever been published), and a repair after a
 * background refresh failed — which is deliberately silent, so it needs
 * somewhere to become visible. The staleness banner is the point of the page;
 * the Rebuild button is the fix.
 */

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '1.25rem 1.5rem',
    marginBottom: '1.25rem',
  },
  banner: (tone) => ({
    borderRadius: '6px',
    padding: '0.75rem 1rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
    background: tone === 'ok' ? '#f0f7f0' : '#fff6e5',
    border: `1px solid ${tone === 'ok' ? '#c3e0c3' : '#f0d09a'}`,
    color: tone === 'ok' ? '#2a5a2a' : '#7a5200',
  }),
  meta: { fontSize: '0.85rem', color: '#666', margin: '0.15rem 0' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: {
    textAlign: 'left',
    borderBottom: '2px solid #e0e0e0',
    padding: '0.4rem 0.5rem',
    whiteSpace: 'nowrap',
  },
  td: { borderBottom: '1px solid #f0f0f0', padding: '0.4rem 0.5rem' },
  mono: { fontFamily: 'monospace' },
  keyList: { margin: '0.4rem 0 0 1.1rem', padding: 0, lineHeight: 1.6 },
};

/** Render a Firestore Timestamp / Date / missing value as local text. */
function formatStamp(value) {
  if (!value) return 'never';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? 'unknown' : date.toLocaleString();
}

export default function EventIndexManager() {
  const [index, setIndex] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [live, current] = await Promise.all([
        loadPublishedEventIndex(),
        loadCurrentEventPairs(),
      ]);
      setIndex(live);
      setPairs(current);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleRebuild() {
    setPublishing(true);
    setError(null);
    setNotice(null);
    try {
      const result = await publishEventIndex();
      setNotice(`Rebuilt: ${result.eventCount} event(s) indexed.`);
      await load();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setPublishing(false);
    }
  }

  const events = index?.events || [];
  const diff = diffEventIndex(index ? events : null, pairs);

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo="/admin" backLabel="Global Admin" title="Global Event Index">
          <button className="btn" onClick={handleRebuild} disabled={publishing || loading}>
            {publishing ? 'Rebuilding…' : 'Rebuild index'}
          </button>
        </PageHeader>

        <div style={styles.card}>
          <p style={{ marginTop: 0 }}>
            A single pre-calculated document — <code>{EVENT_INDEX_PATH}</code> — listing
            every club&apos;s events and their published race dates, so the app answers
            &ldquo;what&apos;s on, where, and does this boat race it&rdquo; in one read
            instead of walking every club. It is rebuilt automatically whenever a race
            schedule is published; rebuild it here after a failed refresh, or to create
            it for the first time.
          </p>
          <p style={styles.meta}>Last generated: {formatStamp(index?.generated_at)}</p>
          <p style={styles.meta}>
            Indexed events: {index ? events.length : 0} &middot; live events: {pairs.length}
          </p>
        </div>

        {error && <div style={styles.banner('warn')}>Error: {error}</div>}
        {notice && <div style={styles.banner('ok')}>{notice}</div>}

        {loading && <p>Loading…</p>}

        {!loading && !index && (
          <div style={styles.banner('warn')}>
            The event index has never been published. Nothing reading it will see any
            events until you rebuild.
          </div>
        )}

        {!loading && index && diff.inSync && (
          <div style={styles.banner('ok')}>In sync with the live clubs and events.</div>
        )}

        {!loading && index && !diff.inSync && (
          <div style={styles.banner('warn')}>
            <strong>Out of date.</strong>
            {diff.missing.length > 0 && (
              <>
                <div style={{ marginTop: '0.4rem' }}>
                  Published but NOT in the index — invisible to the app until you rebuild:
                </div>
                <ul style={styles.keyList}>
                  {diff.missing.map((k) => <li key={k} style={styles.mono}>{k}</li>)}
                </ul>
              </>
            )}
            {diff.orphaned.length > 0 && (
              <>
                <div style={{ marginTop: '0.4rem' }}>
                  In the index but no longer live (deleted since the last rebuild):
                </div>
                <ul style={styles.keyList}>
                  {diff.orphaned.map((k) => <li key={k} style={styles.mono}>{k}</li>)}
                </ul>
              </>
            )}
          </div>
        )}

        {!loading && events.length > 0 && (
          <div style={styles.card}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Club</th>
                  <th style={styles.th}>Event</th>
                  <th style={styles.th}>Access</th>
                  <th style={styles.th}>Classes</th>
                  <th style={styles.th}>Race dates</th>
                  <th style={styles.th}>First</th>
                  <th style={styles.th}>Last</th>
                  <th style={styles.th}>Position</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={`${e.club_id}/${e.event_id}`}>
                    <td style={styles.td}>
                      {e.club_name}
                      <div style={{ ...styles.mono, color: '#888' }}>{e.club_id}</div>
                    </td>
                    <td style={styles.td}>
                      {e.event_name}
                      <div style={{ ...styles.mono, color: '#888' }}>{e.event_id}</div>
                    </td>
                    <td style={styles.td}>{e.access}</td>
                    <td style={styles.td}>{(e.classes || []).length}</td>
                    <td style={styles.td}>{(e.race_dates || []).length}</td>
                    <td style={styles.td}>{e.first_race_date || '—'}</td>
                    <td style={styles.td}>{e.last_race_date || '—'}</td>
                    <td style={styles.td}>
                      {e.club_location
                        ? `${e.club_location.lat.toFixed(3)}, ${e.club_location.lng.toFixed(3)}`
                        : 'unknown'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
